/**
 * HTTP service for adapter management endpoints.
 */

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../http/http.service';

export interface SplunkAdapterConfig {
  adapterType: 'splunk';
  hecUrl: string;
  hecToken: string;
  index: string;
}

export interface SentinelAdapterConfig {
  adapterType: 'sentinel';
  tenantId: string;
  clientId: string;
  clientSecret: string;
  dceUri: string;
  dcrImmutableId: string;
  streamName: string;
}

export type AdapterConfig = SplunkAdapterConfig | SentinelAdapterConfig;

export interface CreateAdapterRequest {
  name: string;
  description?: string;
  config: AdapterConfig;
}

export interface AdapterConnectionStatus {
  status: 'healthy' | 'unhealthy' | 'unknown';
  lastCheckedAt?: string;
  message?: string;
}

export interface AdapterResponse {
  id: string;
  zenityAccountId: string;
  name: string;
  description: string;
  config: AdapterConfig;
  connectionStatus: AdapterConnectionStatus;
  createdAt: string;
  createdBy: string;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
  enabled: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AdaptersHttpService {
  private readonly _adaptersBaseUrl: string = 'adapters';

  constructor(private readonly _httpService: HttpService) {}

  /**
   * Get all adapters for the account.
   *
   * @return Observable of adapter list
   */
  public getAllAdapters$(): Observable<AdapterResponse[]> {
    const url = this._adaptersBaseUrl;
    return this._httpService.get<AdapterResponse[]>(url);
  }

  /**
   * Get a specific adapter by ID.
   *
   * @param adapterId - The adapter ID
   * @return Observable of adapter
   */
  public getAdapterById$(adapterId: string): Observable<AdapterResponse> {
    const url = `${this._adaptersBaseUrl}/${adapterId}`;
    return this._httpService.get<AdapterResponse>(url);
  }

  /**
   * Create a new adapter.
   *
   * @param adapter - The adapter creation request
   * @return Observable of created adapter
   */
  public createAdapter$(adapter: CreateAdapterRequest): Observable<AdapterResponse> {
    const url = this._adaptersBaseUrl;
    return this._httpService.post<CreateAdapterRequest, AdapterResponse>(url, adapter);
  }

  /**
   * Update an existing adapter.
   *
   * @param adapterId - The adapter ID to update
   * @param adapter - The adapter update request
   * @return Observable of updated adapter
   */
  public updateAdapter$(
    adapterId: string,
    adapter: Partial<CreateAdapterRequest>
  ): Observable<AdapterResponse> {
    const url = `${this._adaptersBaseUrl}/${adapterId}`;
    return this._httpService.patch<Partial<CreateAdapterRequest>, AdapterResponse>(url, adapter);
  }

  /**
   * Delete an adapter.
   *
   * @param adapterId - The adapter ID to delete
   * @return Observable of success message
   */
  public deleteAdapter$(adapterId: string): Observable<{ message: string }> {
    const url = `${this._adaptersBaseUrl}/${adapterId}`;
    return this._httpService.delete<{ message: string }>(url);
  }
}
